import apiClient from '../services/apiClient';
import AuthService from '../../modules/Admin/services/AuthService';
import { Roles } from '../../routes/roles';

export const SUBSCRIPTION_BLOCKED_KEY = 'nf_subscription_blocked_v1';

let inflight = null;
let cachedActive = null;

export function isSubscriptionBlocked() {
  try {
    return sessionStorage.getItem(SUBSCRIPTION_BLOCKED_KEY) === '1';
  } catch {
    return false;
  }
}

export function markSubscriptionInactive() {
  cachedActive = false;
  try {
    sessionStorage.setItem(SUBSCRIPTION_BLOCKED_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function clearSubscriptionBlocked() {
  cachedActive = null;
  inflight = null;
  try {
    sessionStorage.removeItem(SUBSCRIPTION_BLOCKED_KEY);
  } catch {
    /* ignore */
  }
}

export function isSubscriptionBypassUrl(url = '') {
  const u = String(url);
  return (
    u.includes('/Subscription/GetMySubscriptionStatus') ||
    u.includes('/shared/auth/login') ||
    u.includes('/shared/auth/refresh') ||
    u.includes('/shared/auth/logout-session') ||
    u.includes('/shared/auth/logout') ||
    u.includes('/marketplace/')
  );
}

function hasUsableAccessToken() {
  const token = AuthService.getToken();
  return !!token && !AuthService.isTokenExpired(token);
}

/** Returns true when tenant may use the app; SuperAdmin always true. */
export async function checkTenantSubscriptionActive() {
  const role = AuthService.getLoggedInRoleId();
  const roleUpper = String(role || '').toUpperCase();
  const superAdminGuid = Roles.SuperAdmin.toUpperCase();
  const marketplaceBuyerGuid = Roles.MarketplaceBuyer.toUpperCase();

  if (roleUpper === marketplaceBuyerGuid) {
    cachedActive = true;
    return true;
  }

  // SuperAdmin is always active
  if (roleUpper === superAdminGuid) {
    clearSubscriptionBlocked();
    return true;
  }

  const clientId = AuthService.getLoggedInClientId();
  if (!clientId) return true;

  // Avoid firing an unauthenticated request during session bootstrap (shows as 401 in DevTools).
  if (!hasUsableAccessToken()) return true;

  try {
    const res = await apiClient.get(`/Subscription/GetMySubscriptionStatus`);
    const active = !!res.data?.hasActiveSubscription;
    if (active) {
      clearSubscriptionBlocked();
      cachedActive = true;
    } else {
      markSubscriptionInactive();
    }
    return active;
  } catch (error) {
    if (error.response?.status === 402) {
      markSubscriptionInactive();
      return false;
    }
    return true;
  }
}

export function ensureSubscriptionChecked() {
  const role = AuthService.getLoggedInRoleId();
  const roleUpper = String(role || '').toUpperCase();
  // SuperAdmin bypasses subscription check
  if (roleUpper === Roles.SuperAdmin.toUpperCase() || !AuthService.getLoggedInClientId()) {
    return Promise.resolve(true);
  }
  if (isSubscriptionBlocked()) return Promise.resolve(false);
  if (cachedActive === true) return Promise.resolve(true);
  if (!inflight) {
    inflight = checkTenantSubscriptionActive()
      .catch(() => true)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}
